'use client';
import cx from 'classnames';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { sendGAEvent } from '@next/third-parties/google';
import React, { useEffect, useRef, useState } from 'react';

import { comscoreService } from '../../../services/comscore';
import { useShouldDisplayBanner } from './AdBanner.hooks';
import styles from './AdBanner.module.scss';
import {
  AdBannerConfig,
  AdBannerInstance,
  AdBannerProps,
  EAdBannerSize,
  sizeMap,
} from './AdBanner.types';

type BannersConfig = Record<EAdBannerSize, AdBannerConfig[]> & {
  instances?: Record<string, AdBannerInstance>;
};

// Cached promise so the JSON is fetched once per session, not once per component
let configPromise: Promise<BannersConfig> | null = null;

// Fallback when banners.json is missing or no banners match the current route
const DEFAULT_BANNER: AdBannerConfig = {
  image: 'banner.gif',
  link: null,
  durationMs: 10000,
  alt: 'Publicidad',
};

/** @internal — only for unit tests, resets the module-level fetch cache */
export const __resetBannerCache = () => {
  configPromise = null;
};

function fetchBannersConfig() {
  if (!configPromise) {
    configPromise = fetch('/ad-banners/banners.json')
      .then((res): Promise<BannersConfig> => {
        if (!res.ok) return Promise.resolve({} as BannersConfig);
        return res.json() as Promise<BannersConfig>;
      })
      .catch((): BannersConfig => {
        configPromise = null; // allow retry on next mount if file wasn't available
        return {} as BannersConfig;
      });
  }
  return configPromise;
}

export const AdBanner: React.FC<AdBannerProps> = ({
  size,
  alt,
  text = 'Publicidad',
  backgroundColor = '#373B3D',
  textColor = '#FFF',
  customSize,
  hideAtWidth,
  hideAtHeight,
  className,
  startIndex = 0,
  fixed = false,
  instanceId,
  hideMobile = true,
}) => {
  const pathname = usePathname();
  const { width, height } = sizeMap[size] ?? customSize!;
  const shouldDisplay = useShouldDisplayBanner(size, hideAtWidth, hideAtHeight);

  const [banners, setBanners] = useState<AdBannerConfig[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFixed, setFixed] = useState(fixed);
  const [imgError, setImgError] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load config on mount, filter by current pathname
  useEffect(() => {
    fetchBannersConfig().then((config) => {
      // Resolve instance overrides from config
      const instance = instanceId ? config.instances?.[instanceId] : undefined;
      const resolvedStartIndex = instance?.startIndex ?? startIndex;
      const resolvedFixed = instance?.fixed ?? fixed;

      const slot = config[size] ?? [];
      const filtered = slot.filter((b) => {
        if (!b.routes?.length) return true; // no routes = universal
        return b.routes.some(
          (r) => pathname === r || (r !== '/' && pathname.startsWith(r + '/')),
        );
      });
      const final = filtered.length > 0 ? filtered : [DEFAULT_BANNER];
      setBanners(final);
      setCurrentIndex(final.length > 0 ? resolvedStartIndex % final.length : 0);
      if (resolvedFixed) setFixed(true);
    });
  }, [size, startIndex, fixed, instanceId, pathname]);

  // Rotate to next banner after each banner's own durationMs
  useEffect(() => {
    if (isFixed || banners.length <= 1) return;

    setImgError(false);
    const current = banners[currentIndex];
    timeoutRef.current = setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % banners.length);
    }, current.durationMs);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [currentIndex, banners]);

  if (!shouldDisplay || banners.length === 0) return null;

  const current = banners[currentIndex];
  const src = `/ad-banners/${size}/${current.image}`;
  const altText = alt ?? current.alt ?? 'Publicidad';
  const link = current.link
    ? current.link.includes('?')
      ? `${current.link}&utm_source=rollinews.com`
      : `${current.link}?utm_source=rollinews.com`
    : null;

  const image = imgError ? (
    <div
      className={styles.adBanner}
      style={{
        width,
        height,
        backgroundColor,
        color: textColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {text}
    </div>
  ) : (
    <Image
      key={currentIndex}
      src={src}
      alt={altText}
      width={width}
      height={height}
      unoptimized
      loading="eager"
      onError={() => setImgError(true)}
    />
  );

  const handleBannerClick = () => {
    const analytics = comscoreService.getAnalytics();
    analytics?.notifyUxActive();

    if (typeof window !== 'undefined' && (window as any).COMSCORE) {
      (window as any).COMSCORE.beacon({
        c1: '4',
        c2: '38962608',
        ns_type: 'click',
        ns_banner_size: size,
        ns_banner_image: current.image,
        ns_banner_link: link,
      });
    }

    sendGAEvent('event', 'ad_banner_click', {
      banner_size: size,
      banner_image: current.image,
      banner_link: link,
      instance_id: instanceId ?? null,
      banner_index: currentIndex,
      banner_alt: current.alt ?? null,
      page_path: pathname,
    });
  };

  return (
    <div
      className={cx(styles.adBannerContainer, className, {
        [styles.hideMobile]: hideMobile,
      })}
    >
      {link ? (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleBannerClick}
        >
          {image}
        </a>
      ) : (
        image
      )}
    </div>
  );
};
